import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { DrRequestInfo } from '../../../models/dr-request-info';
import { DREnumType, Endpoint, HelpIndex, MembershipType } from '../../../models/enums';
import { CellTemplateType, GenericColDef, GridContext } from '../../../models/grid-context';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { GenericGridComponent } from '../../generic-grid/generic-grid.component';
import { NameDescriptionComponent } from '../../name-description/name-description.component';
import { MembershipModalComponent } from '../membership-modal/membership-modal.component';
import { Role } from '../../../models/role';
import { OrganizationViewModel } from '../../../models/organization';
import { createOrgNameOrIdRoleHandles, isNullOrUndefined } from '../../../models/utils';
import { DrObject } from '../../../models/dr-object';

@Component({
    standalone: false,
    selector: 'res-new-organization-modal',
    templateUrl: './new-organization-modal.component.html',
    styleUrls: ['./new-organization-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class NewOrganizationModalComponent extends BaseModalComponent implements OnInit {
    @ViewChild(NameDescriptionComponent, { static: true }) nameDescriptionComponent: NameDescriptionComponent;
    @ViewChild('membersGrid', { static: true }) membersGrid: GenericGridComponent;

    validateNameRequest = {
        endpoint: Endpoint.viewManager_validateName,
        request: (name) => {
            return {
                name: name,
                objectType: this.nameDescriptionComponent.drObject.objectType,
                systemScope: true
            };
        }
    };

    isBusy = false;
    isBusyMessage: string;
    isSettingsBusy = false;
    isDescriptionHidden = true;

    roleList: Array<any>;
    allMembers: Array<any>;
    modalClass = 'new-organization-modal';
    modalAutomationId = 'new-organization';
    modalHelpIndex = HelpIndex.sys_add_org_dlg;
    logoModel = { encodedData: null, fileName: "" };
    initialData: Array<any>;

    membersColumnDefs = new Array<GenericColDef>(
        {
            headerName: 'Type',
            field: 'type',
            automationId: this.modalAutomationId,
            cellTemplateType: CellTemplateType.nameIconLabel,
            width: 200,
            lockVisible: true
        },
        {
            headerName: 'Name',
            field: 'name',
            sortBy: 'name',
            automationId: this.modalAutomationId,
            lockVisible: true
        },
        {
            headerName: 'Role',
            field: 'selectedRole.name',
            automationId: this.modalAutomationId,
            width: 320,
            lockVisible: true
        }
    );
    membersRequestInfo = new DrRequestInfo({
        endpoint: Endpoint.adminOrgManager_listUsersAndGroups,
        data: { organizationName: 'super_system_customer', onlyGroups: false, onlyUsers: false, systemScope: true },
        responseName: 'members'
    });
    membersGridContext = new GridContext({
        sortBy: 'name',
        sortAscending: true
    });
    membersResponseCallback = (response: any) => {
        if (response) {
            response.members = [];
            this.allMembers = [];
            if (response.groups) {
                response.groups.forEach(entity => {
                    entity.type = 'System Group';
                    entity.iconClass = 'fa-users color-purple-03';
                    entity.selectedRole = this.getUserRole(Role.organizationAdministrator);
                    this.allMembers.push(entity);
                });
            }
            if (response.users) {
                response.users.forEach(entity => {
                    entity.type = 'System User';
                    entity.iconClass = 'fa-user color-purple-03';
                    entity.name = entity.userName;
                    entity.selectedRole = this.getUserRole(Role.organizationAdministrator);
                    this.allMembers.push(entity);
                    if (entity.userName === this.commonService.currentUser.userName) {
                        entity.selectedRole = this.getUserRole(Role.organizationAdministrator);
                        response.members.push(entity);
                    }
                });
            }
        }
    }

    constructor(
        private modalService: BsModalService,
        private modalRef: BsModalRef,
        private restService: RestService,
        private commonService: CommonService) {
        super(modalService, modalRef);
    }

    ngOnInit(): void {
        this.nameDescriptionComponent.drObject = new OrganizationViewModel();
        this.allMembers = [];
        this.loadDefaultRoles();
    }

    loadDefaultRoles(): void {
        this.restService.post(Endpoint.realmManager_getDREnumNames, {
            enumType: DREnumType.ORG_DEFAULT_ROLE,
            systemScope: true
        }).then(responseListRoles => {
            if (responseListRoles?.names) {
                this.roleList = []
                responseListRoles.names.forEach(role => {
                    this.roleList.push({ handle: '', name: role })
                })
            }
        });
    }

    getUserRole(roleName: string): any {
        if (roleName) {
            return this.roleList.filter(r => r.name === roleName).pop();
        }
        return null;
    }

    editMembers(): void {
        this.commonService.openModal(MembershipModalComponent, {
            class: 'vertical-percent-80',
            modalData: {
                leftList: this.allMembers,
                rightList: this.membersGrid.getData(),
                membershipType: MembershipType.organization,
                organizationName: this.nameDescriptionComponent.name,
                roleOptions: this.roleList,
                isSystemGroupsOrSystemOrgs: true,
                isNewOrgCreation: true
            }
        }).then((result) => {
            if (result.modalResult && result.modalData.length) {
                const members = [];
                result.modalData.forEach(element => {
                    members.push(element.data);
                });
                this.membersGrid.setData(members);
            }
        });
    }

    okEnabled(): boolean {
        return !this.isBusy && this.nameDescriptionComponent.formValid && !DrObject.isEmpty(this.nameDescriptionComponent.name);
    }

    ok(): void {
        this.isBusy = true;
        this.nameDescriptionComponent.validate().then(valid => {
            if (valid) {
                this.isBusyMessage = 'Creating Organization ...';
                if (!isNullOrUndefined(this.nameDescriptionComponent.name)) {
                    const userNames = [], userRoles = [], groupNames = [], groupRoles = [];
                    this.membersGrid.getData().forEach((member) => {
                        if (member.type === 'System User') {
                            userNames.push(member?.name);
                            userRoles.push(member?.selectedRole?.name)
                        } else {
                            groupNames.push(member?.name);
                            groupRoles.push(member?.selectedRole?.name)
                        }
                    });

                    const userNameRoles = createOrgNameOrIdRoleHandles(userNames, userRoles);
                    const groupNameRoles = createOrgNameOrIdRoleHandles(groupNames, groupRoles);

                    const requestData = {
                        organizationName: this.nameDescriptionComponent.name,
                        description: this.nameDescriptionComponent.description,
                        userRoleName: userNameRoles,
                        groupRoleName: groupNameRoles,
                        systemScope: true,
                        contextHandle: 'super_system_customer'
                    }
                    this.restService.post(Endpoint.realmManager_expressProvision, requestData).then(response => {
                        this.isBusy = false;
                        if (response?.organization) {
                            this.closeModal(true, response.organization);
                        }
                    }, () => {
                        this.isBusy = false;
                    });
                }
            } else {
                this.isBusy = false;
            }
        });
    }

    tooltipPrimaryButton(): string {
        let tooltip = '';
        if (!this.nameDescriptionComponent.formValid) {
            tooltip = 'Please complete all required fields';
        }
        return tooltip;
    }
}
